import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { workspaceApi } from '../services/api';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import './WorkspaceList.css';

const ArticleTemplates = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);

  const author = user?.username || localStorage.getItem('hive_username');

  const load = useCallback(async () => {
    if (!author) return;
    setLoading(true);
    setError('');
    try {
      const data = await workspaceApi.listTemplates(author);
      setTemplates(data.templates || []);
    } catch {
      setError('Failed to load templates.');
    } finally {
      setLoading(false);
    }
  }, [author]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await workspaceApi.deleteTemplate(id, author);
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch {
      alert('Failed to delete template.');
    } finally {
      setDeleting(null);
    }
  };

  if (!isAuthenticated) {
    return <div className="workspace-list-page"><p>Please log in.</p></div>;
  }

  return (
    <>
      <SidebarHeader onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
      <div className="workspace-list-page">
        <div className="workspace-list-header">
          <h2>Templates</h2>
          <Link to="/create-article/create-template" className="workspace-new-btn">+ New Template</Link>
        </div>

        {loading && <LoadingSpinner text="Loading templates..." />}
        {error && <div className="workspace-error">{error}<button onClick={load}>Retry</button></div>}

        {!loading && !error && templates.length === 0 && (
          <div className="workspace-empty">
            <p>No templates yet.</p>
            <Link to="/create-article/create-template" className="workspace-new-btn">Create one</Link>
          </div>
        )}

        {!loading && templates.length > 0 && (
          <div className="workspace-item-list">
            {templates.map(tpl => (
              <div key={tpl.id} className="workspace-item">
                <div className="workspace-item-info" onClick={() => navigate(`/create-article?template=${tpl.id}`)} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && navigate(`/create-article?template=${tpl.id}`)}>
                  <div className="workspace-item-title">{tpl.title || <em>Untitled</em>}</div>
                  <div className="workspace-item-meta">
                    {tpl.community && <span>{tpl.community}</span>}
                    <span>Updated {new Date(tpl.updated_at).toLocaleString()}</span>
                  </div>
                </div>
                <div className="workspace-item-actions">
                  <button className="workspace-action-btn" onClick={() => navigate(`/create-article/create-template?template=${tpl.id}`)}>Edit</button>
                  <button className="workspace-action-btn" onClick={() => navigate(`/create-article?template=${tpl.id}`)}>Use</button>
                  <button className="workspace-action-btn workspace-delete-btn" onClick={() => handleDelete(tpl.id)} disabled={deleting === tpl.id}>
                    {deleting === tpl.id ? '…' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default ArticleTemplates;
